import * as React from 'react';
import { SyntheticEvent, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Breakpoint, Color, HeaderMenuIconButtonPositionsGroup, HeaderMenuTheme, IconName, LogoTheme } from '../../enums';
import { HeaderMenuPosition, MenuPositionData } from '../../interfaces';
import { Overlay } from '../../theme';
import { UseState } from '../../types';
import { mapToBaseMenuPosition } from '../../utils';
import { Icon } from '../icon/icon.component';

import { IconButton } from './components';
import {
  BackButton,
  BottomMobilePosition,
  BottomMobilePositions,
  CloseButton,
  Container,
  DesktopMenuPosition,
  IconsContainer,
  InnerContainer,
  LogoArea,
  LogoButton,
  MenuPositions,
  MenuPositionWrapper,
  MobileMenu,
  MobileMenuTitle,
  MobileMenuTopHeader,
  StyledLogo,
  SubMenuSection,
  TopMobilePosition,
  TopMobilePositions,
  Wrapper
} from './headerMenu.styled';
import { HeaderMenuProps } from './headerMenu.types';

export const HeaderMenu: React.FC<HeaderMenuProps> = (props: HeaderMenuProps) => {
  const {
    menuPositions,
    headerMenuTheme,
    onSearchIconClick,
    onUserIconClick,
    onCartIconClick,
    onLogoClick,
    logoCaption,
    withBottomBorder = false,
    className
  }: HeaderMenuProps = props;
  const isSmallDesktop: boolean = useMediaQuery({ query: Breakpoint.SmallDesktop });
  const isMobile: boolean = useMediaQuery({ query: Breakpoint.Mobile });
  const [isMobileMenuOpen, setIsMobileMenuOpen]: UseState<boolean> = useState<boolean>(false);
  const desktopMenuPositions: MenuPositionData[] = menuPositions
    .filter((position: HeaderMenuPosition): boolean => !!position.displayOnDesktop)
    .map(mapToBaseMenuPosition);
  const topMobilePositions: MenuPositionData[] = menuPositions
    .filter((position: HeaderMenuPosition): boolean => position.positionOnMobile === 'top')
    .map(mapToBaseMenuPosition);
  const bottomMobilePositions: MenuPositionData[] = menuPositions
    .filter((position: HeaderMenuPosition): boolean => position.positionOnMobile === 'bottom')
    .map(mapToBaseMenuPosition);
  const [mobilePositionsTree, setMobilePositionsTree]: UseState<MenuPositionData[]> = useState<MenuPositionData[]>([]);
  const [childrenPositions, setChildrenPositions]: UseState<MenuPositionData[]> = useState<MenuPositionData[]>([]);

  const clickOnPosition = (e: SyntheticEvent, position?: HeaderMenuPosition) => {
    if (position?.childrenPositions) {
      e.preventDefault();
      const mobilePositionsTreeClone: MenuPositionData[] = [...mobilePositionsTree];
      mobilePositionsTreeClone.push(position);
      setMobilePositionsTree(mobilePositionsTreeClone);
      setChildrenPositions(position.childrenPositions);
    }
  };

  const closeMobileMenu = (): void => {
    setIsMobileMenuOpen(false);
    // reset children positions
    setTimeout(
      (): void => {
        setChildrenPositions([]);
        setMobilePositionsTree([]);
      },
      500
    );
  };

  const backToParentOnMobileMenu = (): void => {
    const mobilePositionsTreeClone: MenuPositionData[] = [...mobilePositionsTree].slice(0, -1);
    setMobilePositionsTree(mobilePositionsTreeClone);
    setChildrenPositions(mobilePositionsTreeClone.length
      ? mobilePositionsTreeClone[mobilePositionsTreeClone.length - 1]?.childrenPositions ?? []
      : []
    );
  };

  return (
    <Wrapper>
      {/* HEADER MENU START */}
      <Container className={className} $theme={headerMenuTheme}>
        <InnerContainer $withBottomBorder={withBottomBorder}>
          <LogoArea $theme={headerMenuTheme}>
            <LogoButton onClick={onLogoClick} data-testid='logo-button'>
              <StyledLogo
                logoTheme={headerMenuTheme === HeaderMenuTheme.Transparent ? LogoTheme.White : LogoTheme.Classic}
              />
            </LogoButton>
            {/* Podpis stoi OBOK przycisku, nie w środku: to nie jest element klikalny,
                a na stronie głównej serwisu bywa nagłówkiem strony. */}
            {logoCaption}
          </LogoArea>
          {!isSmallDesktop && (
            <MenuPositions>
              {desktopMenuPositions.map((position: HeaderMenuPosition): JSX.Element => (
                <DesktopMenuPosition
                  {...position}
                  key={position.label}
                  $color={headerMenuTheme === HeaderMenuTheme.Transparent ? Color.White : Color.Black}
                />
              ))}
            </MenuPositions>
          )}
          <IconsContainer>
            <IconButton 
              onClickAction={onSearchIconClick}
              dataTestId='search-icon'
              icon={IconName.Loupe}
              color={headerMenuTheme === HeaderMenuTheme.Transparent ? Color.White : Color.Black}
              menuPositions={menuPositions
                .find((menuPosition: HeaderMenuPosition): boolean => menuPosition.name === HeaderMenuIconButtonPositionsGroup.Search)
                ?.childrenPositions ?? []
              }
            />
            {!isMobile && (
              <IconButton
                onClickAction={onUserIconClick}
                dataTestId='user-icon'
                icon={IconName.User}
                color={headerMenuTheme === HeaderMenuTheme.Transparent ? Color.White : Color.Black}
                menuPositions={menuPositions
                  .find((menuPosition: HeaderMenuPosition): boolean => menuPosition.name === HeaderMenuIconButtonPositionsGroup.User)
                  ?.childrenPositions ?? []
                }
              />
            )}
            <IconButton
              onClickAction={onCartIconClick}
              dataTestId='cart-icon'
              icon={IconName.Bag}
              color={headerMenuTheme === HeaderMenuTheme.Transparent ? Color.White : Color.Black}
              menuPositions={menuPositions
                .find((menuPosition: HeaderMenuPosition): boolean => menuPosition.name === HeaderMenuIconButtonPositionsGroup.Cart)
                ?.childrenPositions ?? []
              }
            />
            {isSmallDesktop && (
              <IconButton
                onClickAction={(): void => setIsMobileMenuOpen(true)}
                icon={IconName.Menu}
                color={headerMenuTheme === HeaderMenuTheme.Transparent ? Color.White : Color.Black}
              />
            )}
          </IconsContainer>
        </InnerContainer>
      </Container>
      {/* HEADER MENU END */}
      {/* MOBILE MENU START */}
      {isSmallDesktop && (
        <Wrapper>
          <MobileMenu $isVisible={isMobileMenuOpen}>
            <MobileMenuTopHeader>
              {!childrenPositions.length ? (
                <StyledLogo />
              ) : (
                <BackButton onClick={backToParentOnMobileMenu}>
                  <Icon name={IconName.ArrowLeft15} size={20} />
                </BackButton>
              )}
              {!!mobilePositionsTree.length && (
                <MobileMenuTitle>
                  {mobilePositionsTree[mobilePositionsTree.length - 1].label}
                </MobileMenuTitle>
              )}
              <CloseButton onClick={closeMobileMenu}>
                <Icon
                  name={IconName.Close}
                  size={20}
                  color={Color.Black}
                />
              </CloseButton>
            </MobileMenuTopHeader>
            {!childrenPositions.length ? (
              <Wrapper>
                <TopMobilePositions>
                  {topMobilePositions.map((position: HeaderMenuPosition): JSX.Element => (
                    <TopMobilePosition
                      {...position}
                      key={position.label}
                      action={closeMobileMenu}
                    />
                  ))}
                </TopMobilePositions>
                <BottomMobilePositions>
                  {bottomMobilePositions.map((position: HeaderMenuPosition): JSX.Element => (
                    <MenuPositionWrapper
                      onClick={(e: SyntheticEvent): void => clickOnPosition(e, position)}
                    >
                      <BottomMobilePosition
                        {...position}
                        key={position.label}
                        disabled={!!position?.childrenPositions?.length}
                        action={closeMobileMenu}
                      />
                      {!!position?.childrenPositions?.length && <Icon name={IconName.ArrowRight15} size={20} />}
                    </MenuPositionWrapper>
                  ))}
                </BottomMobilePositions>
              </Wrapper>
            ) : (
              <SubMenuSection>
                {childrenPositions.map((position: HeaderMenuPosition): JSX.Element => (
                  <MenuPositionWrapper
                    onClick={(e: SyntheticEvent): void => clickOnPosition(e, position)}
                  >
                    <BottomMobilePosition
                      {...position}
                      key={position.label}
                      disabled={!!position?.childrenPositions?.length}
                      action={closeMobileMenu}
                    />
                    {!!position?.childrenPositions?.length && <Icon name={IconName.ArrowRight15} size={20} />}
                  </MenuPositionWrapper>
                ))}
              </SubMenuSection>
            )}
          </MobileMenu>
          {/* MOBILE MENU END */}
          <Overlay
            $isVisible={isMobileMenuOpen}
            onClick={closeMobileMenu}
          />
        </Wrapper>
      )}
    </Wrapper>
  );
};
